<template>
  <VRow no-gutters>
    <VCol class="px-0">
      <VExpansionPanels
        v-model="panel"
        variant="accordion"
        class="pa-0 ma-0 demo-finder"
      >
        <SLoadingOverlay :loading="loading" />
        <VExpansionPanel v-if="hasHistory" data-testid="recentDemonstrators" class="box">
          <VExpansionPanelTitle class="background-grey text-uppercase font-weight-medium font16 expansion-header" :expand-icon="icons[panel === panelCount - 3 ? 'mdiMinus' : 'mdiPlus']">
            {{ $t('demonstrator.label.recent') }}
          </VExpansionPanelTitle>
          <VExpansionPanelText class="px-0">
            <DemoByHistoryComponent
              :recent-demonstrators="recentDemonstrators"
              @demo-selected="demoSelected"
            />
          </VExpansionPanelText>
        </VExpansionPanel>
        <VExpansionPanel data-testid="find-demo-by-name" class="box">
          <VExpansionPanelTitle class="background-grey text-uppercase font-weight-medium font16 expansion-header" :expand-icon="icons[panel === panelCount - 2 ? 'mdiMinus' : 'mdiPlus']">
            {{ $t('demonstrator.label.byname') }}
          </VExpansionPanelTitle>
          <VExpansionPanelText class="px-0">
            <DemoByNameComponent
              :name-endpoint="nameEndpoint"
              @demo-selected="demoSelected"
            />
          </VExpansionPanelText>
        </VExpansionPanel>
        <VExpansionPanel data-testid="find-demo-nearme" class="box">
          <VExpansionPanelTitle  class="background-grey text-uppercase font-weight-medium font16 expansion-header" :expand-icon="icons[panel === panelCount - 1 ? 'mdiMinus' : 'mdiPlus']">
            {{ $t('demonstrator.label.nearme') }}
          </VExpansionPanelTitle>
          <VExpansionPanelText class="px-0">
            <DemoByLocationComponent
              :postal-code="postalCode"
              :proximity-endpoint="proximityEndpoint"
              :location-endpoint="locationEndpoint"
              @demo-selected="demoSelected"
            />
          </VExpansionPanelText>
        </VExpansionPanel>
        <hr class="border-bottom">
      </VExpansionPanels>
    </VCol>
  </VRow>
</template>

<script lang="ts" setup>
import { mdiMinus, mdiPlus } from '@mdi/js'
import DemoByHistoryComponent from '~/components/account/demonstrator/DemonstratorByHistory.vue'
import DemoByNameComponent from '~/components/account/demonstrator/DemonstratorByName.vue'
import DemoByLocationComponent from '~/components/account/demonstrator/DemonstratorByLocation.vue'
import { useAccountStore } from '~/stores/account'
import type { Demonstrator } from '~/types/account'
import { UserRole } from '~/types/account'

const icons = { mdiMinus, mdiPlus }

const props = withDefaults(defineProps<{
  showHistory?: boolean,
  defaultOpen?: boolean,
  alternatePostalCode?: string,
  nameEndpoint?: string,
  proximityEndpoint?: string,
  locationEndpoint?: string
}>(), {
  showHistory: true,
  defaultOpen: true,
  alternatePostalCode: '',
  nameEndpoint: 'demonstrator/by-name',
  proximityEndpoint: 'demonstrator/proximity',
  locationEndpoint: 'demonstrator/location'
})
const emits = defineEmits<{ (e: 'demo-selected', demonstrator: Demonstrator): void }>()

const { user } = useSAuthState()
const accountStore = useAccountStore()

const postalCode = ref('')
const panel = ref(-1)

const recentDemonstrators = computed(() => accountStore.recentDemonstrators)
const hasHistory = computed(() => {
  return user.value.role === UserRole.Customer && recentDemonstrators.value?.length && props.showHistory
})
const panelCount = computed(() => hasHistory.value ? 3 : 2)

const demoSelected = (demonstrator: Demonstrator) => {
  panel.value = props.defaultOpen ? panelCount.value - 2 : -1
  emits('demo-selected', demonstrator)
}

const { loading, processAction } = useAction()
onMounted(() => {
  processAction(async () => {
    if (user.value.role === UserRole.Customer) {
      postalCode.value = user.value.address?.postalCode ?? ''
      await accountStore.fetchDemosByHistory()
    }
    if (postalCode.value === '') {
      postalCode.value = props.alternatePostalCode ?? ''
    }
    if (props.defaultOpen) {
      panel.value = panelCount.value - 2
    }
  })
})
</script>

<style lang="scss" scoped>
.outline {
  border: 1px solid rgb(var(--v-theme-border));
}
.outline + .outline {
  border-top: 1px solid transparent;
}
.demo-radio {
  font-size: 1rem;
  margin: 14px 0px;
  padding-left: 16px;
}
:deep(.v-label) {
  position: inherit;
}
.background-grey {
  background-color: rgb(var(--v-theme-backgroundDiv));
}
:deep(.v-expansion-panel-text__wrapper) { padding: 0; }
.expansion-header {
  min-height: 48px;
  height: 48px;
}
:deep(.demo-finder .v-expansion-panel__shadow) {
  box-shadow: none;
}
.box {
  border-left: 1px solid rgb(var(--v-theme-border));
  border-right: 1px solid rgb(var(--v-theme-border));
}
.border-bottom {
  border-bottom: 1px solid rgb(var(--v-theme-border));
  display: block;
  height: 1px;
  flex: none;
  border-top: none;
}
</style>
